import { Map as MapLibre, Marker } from "maplibre-gl";
import { createEffect, createSignal, For, onMount } from "solid-js";

import { InputField } from "./InputField";
import type { Coords, GeocodeResponse } from "../types";
import { panic } from "../util/panic";

const defaultZoom = 4.5;

type GeocodeProps = {
    coords: Coords;
    setCoords: (coords: Coords) => any;
};

type Result = { name: string; coords: Coords };

async function fetchGeocode(name: string) {
    const url = new URL("https://data.geopf.fr/geocodage/search/");
    url.searchParams.set("q", name);
    const res = await fetch(url);
    const geocode = await res.json() as GeocodeResponse;
    return (geocode.features ?? []).reduce(
        (acc, val) => {
            if (val.geometry && val.geometry.coordinates
                && val.properties && val.properties.label) {
                acc.push({
                    name: val.properties.label,
                    coords: val.geometry.coordinates,
                });
            }
            return acc;
        },
        [] as Result[]
    );
}

export function Geocode(props: GeocodeProps) {
    const [ results, setResults ] = createSignal<Result[]>([]);

    let carte!: HTMLDivElement;
    let searchRef!: HTMLInputElement;

    let map: MapLibre;
    let marker: Marker;

    createEffect(() => {
        const coords = props.coords;
        if (map === undefined || marker === undefined) {
            return;
        }
        marker.setLngLat(coords);
        map.setCenter(coords);
        map.setZoom(defaultZoom);
    });

    const searchCoords = async (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
        const search = searchRef.value.trim();
        if (search.length === 0) {
            panic("Veuillez entrer plus de lettres.")
        }
        setResults(await fetchGeocode(searchRef.value));
    };

    onMount(() => {
        let style = window.getComputedStyle(document.body).getPropertyValue("--map-url");
        if (style.length == 0) {
            style = "https://openmaptiles.geo.data.gouv.fr/styles/osm-bright/style.json";
        }
        map = new MapLibre({
            container: (carte as any),
            style,
            center: props.coords,
            zoom: defaultZoom,
            zoomSnap: 0.5,
            rollEnabled: false,
            dragRotate: false,
            pitchWithRotate: false,
            attributionControl: false,
        });
        marker = new Marker({
                color: "var(--color-marker)",
                draggable: true,
            })
            .setLngLat(props.coords)
            .addTo(map);

        marker.on("dragend", () => {
            props.setCoords(marker.getLngLat().toArray());
        });
    });

    const setCoords = (coords: Coords) => {
        return (ev: Event) => {
            ev.preventDefault();
            ev.stopPropagation();
            marker.setLngLat(coords);
            props.setCoords(coords);
            map.setCenter(coords);
        };
    }

    return <form class="geocode" onSubmit={searchCoords} autocomplete="off">
        <div class="search">
            <InputField
                ref={searchRef}
                type="text"
                id="geocode-search"
                label="Recherche de coordonnées"
                value=""
                />
            <input type="submit" value="Rechercher"/>
        </div>
        <div class="results">
            <div>
                <ul>
                    <For each={results()}>
                        {(r) => <li><a href="#" onClick={setCoords(r.coords)}>{r.name}</a>
                        <span>lng: {r.coords[0]} lat: {r.coords[1]}</span></li>}
                    </For>
                </ul>
            </div>
        </div>
        <div ref={carte} class="map"></div>
        <div class="info">Il est possible de déplacer le marqueur pour un positionnement plus précis.</div>
    </form>;
}